import {XMarkIcon} from '@heroicons/react/24/outline';
import merge from 'deepmerge';
import React, {forwardRef, ReactElement, useCallback, useEffect, useImperativeHandle, useMemo} from 'react';
import ReactModal from 'react-modal';

import {useBreakpoint} from '../../common/util/useBreakpoint';
import NormalButton from './buttons/NormalButton';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

const baseStyles: ReactModal.Styles = {
  overlay: {position: 'absolute', overflow: 'auto', background: 'rgba(24,24,34,0.5)', zIndex: 10},
  content: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    paddingTop: '18rem',
    left: '50%',
    width: '120rem',
    transform: 'translateX(-50%)',
    border: 0,
    background: undefined,
    display: 'flex',
    flexDirection: 'column',
  },
};

export type ModalHandle = {
  open: () => void;
  close: () => void;
};

export type ModalProps = {
  children: React.ReactNode;
  title?: string;
  beforeOpen?: () => void;
  beforeClose?: () => void;
  afterClose?: () => void;
  left?: React.ReactNode;
  right?: React.ReactNode;
  bottom?: React.ReactNode;
  hideCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  // Whenever this prop changes, modal is closed
  forceReset?: any;
  // If true, the modal will not grow past screen height. Child element is responsible for content scrolling.
  maxHeightScreen?: boolean;
};

const Modal = forwardRef<ModalHandle, ModalProps>(
  (
    {
      children,
      title,
      beforeOpen,
      beforeClose,
      afterClose,
      left,
      right,
      bottom,
      hideCloseButton,
      closeOnClickOutside = true,
      forceReset,
      maxHeightScreen,
    },
    forwardedRef
  ): ReactElement => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const {isMobile} = useBreakpoint('mobile');
    const modalStyles = useMemo(() => {
      if (isMobile) return merge(baseStyles, {content: {width: '100%', paddingLeft: '1rem', paddingRight: '1rem'}});
      return baseStyles;
    }, [isMobile]);

    useEffect(() => {
      void forceReset; // To explicitly have this as dependency
      setModalOpen(false);
    }, [forceReset]);

    useImperativeHandle(forwardedRef, () => ({
      open() {
        openModal();
      },
      close() {
        closeModal();
      },
    }));

    const openModal = useCallback(() => {
      try {
        if (beforeOpen) beforeOpen();
        setModalOpen(true);
      } catch (error) {
        alert(error);
      }
    }, [beforeOpen]);

    const closeModal = useCallback(() => {
      try {
        if (beforeClose) beforeClose();
        setModalOpen(false);
        if (afterClose) afterClose();
      } catch (error) {
        alert(error);
      }
    }, [afterClose, beforeClose]);

    return (
      <ReactModal
        isOpen={modalOpen}
        contentLabel={title}
        style={modalStyles}
        onRequestClose={closeOnClickOutside ? closeModal : undefined}
        ariaHideApp={false}
        parentSelector={() => document.getElementById('envirogameBoard')!}
      >
        <MessageBox
          className="mobile:!px-xs"
          contentClassName={maxHeightScreen ? 'h-full overflow-auto' : undefined}
          style={maxHeightScreen ? {minHeight: '300px'} : undefined}
          top={title && <SimplePill>{title}</SimplePill>}
          topRight={
            !hideCloseButton && (
              <NormalButton light onClick={closeModal} aria-label={'Close dialogue'}>
                <XMarkIcon className="sm w-sm" />
              </NormalButton>
            )
          }
          midLeft={left}
          midRight={right}
          bottom={bottom}
        >
          {children}
        </MessageBox>
      </ReactModal>
    );
  }
);

Modal.displayName = 'Modal';

export default Modal;
